import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ReceiveCouponRequestParams, ReceiveCouponV2RequestParams, receiveCouponV2 } from '@/api/coupon';
import { useQuickScreenCouponList, useUserInfo, useLocationInfo, useReportPageLoaded, useCouponData, useIsOpenCity } from '@/context/PageStateContext';
import { useTracker } from '@/context/TrackerContext';
import { useLogin } from '@/hooks/useLogin';
import { reportCouponWallDisplayStatus, reportCouponWallReceiveSuccessRate, transformData, transformDataV2, getTabPlanCodes } from './utils';
import CouponCard, { CouponCardStyle } from './CouponCard';
import Tab from './Tab';
import ScrollView from '@/components/scroll-view';
import Skeleton from './Skeleton';
import EmptyState from './EmptyState';
import { isServer } from '@/lib/constant';
import {
  CouponWallProps,
  TitleItem,
  CouponWallTab,
  CouponWallItem,
  GrantRightsTab,
  CouponData,
  GrantCouponItem,
  CouponSupply,
  CouponGrantSource,
  SendCouponIdentityScene,
} from '@/types/coupon-wall';
import getUnpl from '@/lib/getUnpl';
import { useToast } from '@/context/ToastContext';
import { Level, reportCustomMetric, reportPerfMetric } from '@/lib/owl';
import Exposure from '@/components/exposure';
import { COUPON_ACTIVITY_SCENE, COUPON_CHANNEL_MAP, CouponWallRenderStatus, CouponWallStatus, ExposureStateEnum, GET_COUPON_WALL_API_MAP, LX_COUPON_CHANNEL_MAP, PreSendStatus, PreviewStatusMap, RECEIVE_COUPON_API_MAP, ReceiveCouponButtonStyle, ReceiveStatus } from './constants';
import recordStep from '@/lib/bridge/recordStep';
import './index.scss';
import { COUPON_WALL_BID_MC, COUPON_WALL_BID_MV } from '@/track/coupon-wall';
import PreviewState from './PreviewState';
import { getClientInfo } from '@/lib/couponUtils/getClientInfo';

/**
 * 券墙组件
 */
export const CouponWall: React.FC<CouponWallProps> = ({
  scene,
  // 券墙相关样式配置
  couponWallStyle = {},
  // 是否开启少量券样式适配，默认不开启
  enableFewCouponStyleFit = false,
  pageCid = '',
  isHitCouponOptimizeExp = false, // 是否命中券优化实验
}) => {
  const { login } = useLogin();
  const tracker = useTracker();
  const { setQuickScreenCouponList, quickScreenCouponList } = useQuickScreenCouponList();
  // 获取 Context 中的券数据
  const { couponData } = useCouponData();
  // 从 SSR 数据中初始化未登录状态
  const [notLoggedIn, setNotLoggedIn] = useState<boolean>(() => {
    return couponData?.isNotLoggedIn || false;
  });
  const [data, setData] = useState<{
    title: TitleItem[];
    tabs: CouponWallTab[];
    coupons: CouponWallItem[];
    currentTime: number;
    countdownAnimationToggle: boolean;
    showCouponValue: boolean;
    recallToken: string;
    activityCode: string;
    activityName: string;
    activityScene: number;
  } | null>(() => {
    if (couponData?.rawData) {
      const transformed = transformData(couponData.rawData);
      return transformed;
    }
    return null;
  });
  const { userInfo } = useUserInfo();
  const { isOpenCity } = useIsOpenCity();
  const { locationInfo } = useLocationInfo();
  const { setModuleLoaded } = useReportPageLoaded();
  const [currentTabId, setCurrentTabId] = useState<string | number>('');
  const [countdown, setCountdown] = useState<number>(3); // 3秒倒计时
  const couponsScrollRef = useRef<HTMLDivElement>(null); // 券列表滚动容器ref
  const hasInitializedRef = useRef(false);
  const toast = useToast();

  // 判断是否存在未领取的券
  const hasUnreceivedCoupons = useMemo(() => {
    const { coupons = [] } = data || {};
    return coupons.some(coupon => !coupon.data?.couponViewId);
  }, [data]);

  // 预发券状态
  const [preSendStatus, setPreSendStatus] = useState<PreSendStatus>(() => {
    return couponData?.isNotLoggedIn || couponData?.rawData
      ? PreSendStatus.SUCCESS
      : PreSendStatus.PENDING;
  });

  // 领券状态
  const [receiveStatus, setReceiveStatus] = useState<ReceiveStatus>(ReceiveStatus.IDLE);
  // 标记是首次请求预发券、还是重试请求预发券，用于区分红包墙展示
  const isRetryPreSendRef = useRef<boolean>(false);
  // 标记是否在预发券返回前触发领券，用于丢弃预发券结果
  const isReceivedBeforePreSendRef = useRef<boolean>(false);

  // 计算红包墙渲染状态
  const couponWallRenderStatus = useMemo(() => {
    // 1. 未登录，展示红包墙兜底图，点击跳转登录页
    if (notLoggedIn) {
      return CouponWallRenderStatus.NOT_LOGIN;
    }

    const hasCouponData = data && data.coupons.length;
    const isPreSendSuccess = preSendStatus === PreSendStatus.SUCCESS;

    // 2. 领券成功：
    //    2.1 有券数据则展示真实券墙
    //    2.2 无券数据，如果命中实验，并且在预发券接口返回前触发领券 isPreSendSuccess = false，此时展示静态图
    //    2.3 无券数据，如果未命中实验，或在预发券接口返回后触发领券 isPreSendSuccess = true，此时展示错误兜底图
    if (receiveStatus === ReceiveStatus.SUCCESS) {
      return hasCouponData
        ? CouponWallRenderStatus.RECEIVED
        : isPreSendSuccess
          ? CouponWallRenderStatus.ERROR
          : CouponWallRenderStatus.LOADING_PREVIEW;
    }

    // 3. 领券中：预发券可用则展示真实预发券墙，否则展示静态图
    if (receiveStatus === ReceiveStatus.RECEIVING) {
      return isPreSendSuccess
        ? CouponWallRenderStatus.RECEIVING
        : CouponWallRenderStatus.RECEIVING_PREVIEW;
    }

    // 4. 领券失败：预发券可用则展示错误兜底图，否则展示静态图
    if (receiveStatus === ReceiveStatus.FAIL) {
      return isPreSendSuccess
        ? CouponWallRenderStatus.ERROR
        : CouponWallRenderStatus.LOADING_PREVIEW;
    }

    // 5. 预发券失败，展示错误兜底图
    if (preSendStatus === PreSendStatus.FAIL) {
      return CouponWallRenderStatus.ERROR;
    }

    // 6. 预发券成功，如果有券数据则展示真实的券墙，否则展示错误兜底图
    if (preSendStatus === PreSendStatus.SUCCESS) {
      return hasCouponData
        ? hasUnreceivedCoupons // 如果所有券都已经领券，则为领券成功状态
          ? CouponWallRenderStatus.HAS_UNRECEIVED_COUPON
          : CouponWallRenderStatus.RECEIVED
        : CouponWallRenderStatus.ERROR;
    }

    // 7. 预发券加载中：
    //    7.1 首次加载且命中实验：展示静态图
    //    7.2 首次加载且未命中实验：展示骨架屏
    //    7.3 重试加载：命中实验，并且预发券结果返回前触发过领券，则展示静态图；否则展示骨架屏
    if (isHitCouponOptimizeExp && (!isRetryPreSendRef.current || isReceivedBeforePreSendRef.current)) {
      return CouponWallRenderStatus.LOADING_PREVIEW;
    }
    return CouponWallRenderStatus.LOADING;
  }, [data, receiveStatus, preSendStatus, notLoggedIn, hasUnreceivedCoupons, isHitCouponOptimizeExp])

  // 判断是否展示单券样式
  const useSingleCouponStyle = useMemo(() => {
    const { coupons = [] } = data || {};
    return enableFewCouponStyleFit && coupons.length === 1;
  }, [data, enableFewCouponStyleFit]);

  // 判断是否展示两行券：仅当券数量 >= 8 时才展示两行
  const useTwoRowCouponStyle = useMemo(() => {
    const { coupons = [] } = data || {};
    return isHitCouponOptimizeExp && !hasUnreceivedCoupons && coupons.length >= 8;
  }, [data, isHitCouponOptimizeExp, hasUnreceivedCoupons]);

  // 计算券墙状态
  const redStatus = useMemo(() => {
    if (couponWallRenderStatus === CouponWallRenderStatus.NOT_LOGIN) {
      return CouponWallStatus.NOT_LOGIN;
    }
    if (couponWallRenderStatus === CouponWallRenderStatus.ERROR) {
      return CouponWallStatus.ERROR;
    }
    if ([CouponWallRenderStatus.HAS_UNRECEIVED_COUPON, CouponWallRenderStatus.LOADING_PREVIEW, CouponWallRenderStatus.RECEIVING_PREVIEW].includes(couponWallRenderStatus)) {
      return CouponWallStatus.NOT_RECEIVED;
    }
    return CouponWallStatus.RECEIVED;
  }, [notLoggedIn, preSendStatus, hasUnreceivedCoupons]);
  // 发券渠道
  const couponChannel = useMemo(() => COUPON_CHANNEL_MAP[scene], [scene]);
  // 发券渠道（埋点上报使用）
  const lxCouponChannel = useMemo(() => LX_COUPON_CHANNEL_MAP[scene], [scene]);

  // 获取券墙数据
  const fetchCouponWall = async (isRetry: boolean = false) => {
    // 如果用户在获取预发券数据之前已经触发领券，则不发起请求
    if (isReceivedBeforePreSendRef.current) {
      return;
    }

    // 标记是否是重试加载，用于区分首次加载和重试加载的展示
    if (isRetry) {
      isRetryPreSendRef.current = true;
    }

    // 上报开始获取数据的性能埋点
    const getCouponDataStartTime = Date.now();
    if (!hasInitializedRef.current && (window as any)?.__PAGE_JS_START__) {
      reportPerfMetric({
        key: "getCouponDataStart",
        value: getCouponDataStartTime - (window as any)?.__PAGE_JS_START__,
      })
    }
    // 记录预发券结果状态，用于上报预发券的成功率
    let couponDisplayStatus = CouponWallRenderStatus.NOT_LOGIN;
    try {
      setPreSendStatus(PreSendStatus.PENDING);
      setReceiveStatus(ReceiveStatus.IDLE);
      setNotLoggedIn(false);

      // 构造请求参数
      const clientInfo = await getClientInfo(scene, false);
      const params = {
        requestType: 0,
        couponActivityScene: COUPON_ACTIVITY_SCENE,
        distributorChannel: couponChannel,
        ...clientInfo,
      }
      // 构建 headers：只有当 token 存在且非空时才添加 token header
      const headers: Record<string, string> = {};
      if (userInfo?.token && userInfo.token.trim() !== '') {
        headers.token = userInfo.token;
      }

      // 请求接口
      const getCouponWallApi = GET_COUPON_WALL_API_MAP[scene];
      const response = await getCouponWallApi(params, {
        ...(Object.keys(headers).length > 0 ? { headers } : {}),
      });
      
      // 如果用户在预发券返回之前触发领券，则丢弃预发券结果
      if (isReceivedBeforePreSendRef.current) {
        return;
      }

      if (response && response.data) {
        const transformed = transformData(response.data);
        setData(transformed);

        // 更新券快筛数据
        setQuickScreenCouponList(transformed.quickScreenCouponList);
        
        if (transformed.tabs.length > 0 && !currentTabId) {
          setCurrentTabId(transformed.tabs[0].planCode);
        }
        setPreSendStatus(PreSendStatus.SUCCESS);
        couponDisplayStatus = Array.isArray(transformed.coupons) && transformed.coupons.some(coupon => !coupon.data?.couponViewId)
          ? CouponWallRenderStatus.HAS_UNRECEIVED_COUPON
          : CouponWallRenderStatus.RECEIVED;
      } else {
        setPreSendStatus(PreSendStatus.FAIL);
        couponDisplayStatus = CouponWallRenderStatus.ERROR;
      }
    } catch (err: any) {
      setPreSendStatus(PreSendStatus.FAIL);
      const errorCode = err?.code || err?.data?.code;
      if (errorCode === 401) {
        setNotLoggedIn(true);
        couponDisplayStatus = CouponWallRenderStatus.NOT_LOGIN;
      } else {
        couponDisplayStatus = CouponWallRenderStatus.ERROR;
      }
    } finally {
      reportCustomMetric({
        key: "fetchCouponCount",
        value: 1,
        tags: {
          isSupportAPI: typeof window !== "undefined" && "IntersectionObserver" in window,
        }
      })
      const getCouponDataEndTime = Date.now();
      setModuleLoaded('couponWall');
      reportCouponWallDisplayStatus(couponDisplayStatus);
      
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        if (!isServer) {
          // 上报请求券墙数据耗时
          reportPerfMetric({
            key: "getCouponDataCost",
            value: getCouponDataEndTime - getCouponDataStartTime,
          })
          // 上报请求券墙数据结束时间
          if ((window as any)?.__PAGE_JS_START__) {
            reportPerfMetric({
              key: "getCouponDataEnd",
              value: getCouponDataEndTime - (window as any)?.__PAGE_JS_START__,
            })
          }
        }
      }
    }
  };

  // 初始化加载数据
  useEffect(() => {
    const { isInited = false } = locationInfo || {};
    // 如果位置信息已初始化且未处理过，并且页面对当前城市开放，则加载数据
    if (isInited && !hasInitializedRef.current && isOpenCity) {
      // 如果 SSR 已返回 401 未登录状态，跳过请求（显示未登录界面）
      // 如果 Context 中有原始数据，并且 SSR、CSR 用户真实定位的三级城市编码相同，则直接复用 SSR 数据
      const initCouponList = couponData?.rawData?.list
      if (couponData?.isNotLoggedIn || (Array.isArray(initCouponList) && initCouponList.length && locationInfo?.ssrDiff?.isSameTownCode)) {
        setModuleLoaded('couponWall');
        hasInitializedRef.current = true;
        reportCustomMetric({
          key: "fetchCouponCount",
          value: 1,
        })
        return;
      }

      fetchCouponWall();
    }
  }, [locationInfo?.isInited]);

  // 处理登录
  const handleLogin = async () => {
    try {
      await login({
        onLoginFail(error) {
          console.error('[CouponWall] login fail:', error);
        }
      });
    } catch (err) {
      console.error('[CouponWall] login error:', err);
    }
  };

  // 滚动到指定Tab的第一张券
  const scrollToTabFirstCoupon = useCallback((tabId: string | number, smooth: boolean = true) => {
    const coupons = data?.coupons || [];
    if (couponsScrollRef.current && coupons.length > 0) {
      // 找到 Tab 对应的有效 planCode 列表
      const targetTab = data?.tabs.find(t => t.planCode === tabId);
      const planCodes = targetTab ? getTabPlanCodes(targetTab) : [tabId as string];
      // 找到第一个匹配的券的索引
      const firstCouponIndex = coupons.findIndex(
        coupon => planCodes.includes(coupon.data?.planCode)
      );
      
      if (firstCouponIndex !== -1) {
        // 使用 setTimeout 确保 DOM 已经渲染
        setTimeout(() => {
          const scrollContainer = couponsScrollRef.current;
          if (!scrollContainer) return;
          
          // 获取券卡片元素
          const couponElements = scrollContainer.children;
          if (couponElements[firstCouponIndex]) {
            const targetElement = couponElements[firstCouponIndex] as HTMLElement;
            // 计算目标元素相对于滚动容器的位置
            const containerLeft = scrollContainer.getBoundingClientRect().left;
            const targetLeft = targetElement.getBoundingClientRect().left;
            const currentScrollLeft = scrollContainer.scrollLeft;
            
            // 计算需要滚动的距离（将目标元素滚动到容器最左侧）
            const scrollTo = currentScrollLeft + (targetLeft - containerLeft) - 20; // 减去padding
            
            // 滚动到目标位置
            scrollContainer.scrollTo({
              left: Math.max(0, scrollTo),
              behavior: smooth ? 'smooth' : 'auto'
            });
          }
        }, 0);
      }
    }
  }, [data?.coupons]);

  // 处理Tab切换
  const handleTabClick = useCallback((tabId: string | number) => {
    setCurrentTabId(tabId);
    // 滚动到对应Tab下的第一张券
    scrollToTabFirstCoupon(tabId, true);
  }, [scrollToTabFirstCoupon]);

  // Tab 滚动结束埋点（由通用 ScrollView 在滚动结束时触发）
  const handleTabsScrollEnd = useCallback(() => {
      tracker?.moduleClick(COUPON_WALL_BID_MC.TAB_SCROLL, {
        coupon_channel: lxCouponChannel,
        exposure_num: useTwoRowCouponStyle ? 1 : 0,
      });
    },
    [lxCouponChannel, useTwoRowCouponStyle],
  );

  // TODO：待下线
  // 上报领券埋点
  const reportReceiveCoupon = (coupons: CouponData[], style: ReceiveCouponButtonStyle) => {
    const couponIds = coupons.map(coupon => coupon.couponConfigId).join(';');
    const valLab = {
      coupon_channel: lxCouponChannel,
      button_style: style, // 领取方式
      discount_coupon_config_id: couponIds,
    };
    tracker?.moduleClick(COUPON_WALL_BID_MC.RECEIVE_BUTTON, valLab);
  };

  // 【旧版】处理领券（点击领取或倒计时自动领取）
  // 未命中实验时使用
  const handleReceiveCoupons = async (buttonStyle: ReceiveCouponButtonStyle) => {
    if (!data) {
      reportCouponWallReceiveSuccessRate(false, "noData");
      reportError({
        name: '[CouponWall] handleReceiveCouponsError',
        msg: '数据未加载，无法领取',
        category: 'couponError',
        level: Level.ERROR,
      })
      return;
    }

    // 如果正在领取中，防止重复点击
    if (receiveStatus === ReceiveStatus.RECEIVING) {
      return;
    }

    try {
      setReceiveStatus(ReceiveStatus.RECEIVING);

      // 1. 拼接入参，参数取值按照ReceiveCouponRequestParams类型创建
      // 构建tabs参数：从当前coupons中按planCode分组，仅包含未领取的券（没有couponViewId）
      const tabsMap = new Map<string, string[]>();
      // 待领取券列表
      const unreceivedCoupons: CouponData[] = [];
      data.coupons.forEach((coupon) => {
        if (coupon.data && !coupon.data.couponViewId) {
          const couponData = coupon.data;
          // 仅包含未领取的券（没有couponViewId）
          unreceivedCoupons.push(couponData);
          const planCode = couponData.planCode;
          if (!tabsMap.has(planCode)) {
            tabsMap.set(planCode, []);
          }
          tabsMap.get(planCode)!.push(couponData.rightCode);
        }
      });

      // 上报领券埋点
      reportReceiveCoupon(unreceivedCoupons, buttonStyle);

      const tabs: GrantRightsTab[] = Array.from(tabsMap.entries()).map(([planCode, rightCodes]) => ({
        planCode,
        rightCodes,
      }));

      const preSendCouponList: GrantCouponItem[] = data.coupons.map(coupon => ({
        couponValue: coupon?.data?.couponValue,
        couponViewId: coupon?.data?.couponViewId,
        couponConfigId: coupon?.data?.couponConfigId,
        planCode: coupon?.data?.planCode,
        rightCode: coupon?.data?.rightCode,
      }));

      const clientInfo = await getClientInfo(scene, true);
      const sessionId = await tracker?.getSessionId().catch(()=> '') || ''

      const params: ReceiveCouponRequestParams = {
        ...clientInfo,
        activityCode: data.activityCode,
        activityName: data.activityName,
        activityScene: data.activityScene,
        recallToken: data.recallToken,
        tabs,
        distributorChannel: couponChannel,
        pageId: pageCid || '',
        moduleId: COUPON_WALL_BID_MV.CARD,
        sessionId,
        unpl: getUnpl() || '',
        preSendCouponList, // 预发券列表
      };
      // 上报是否有 unpl 的自定义指标
      reportCustomMetric({
        key: 'receiveCouponUnplStatus',
        value: params.unpl ? 1 : 0,
        tags: { scene: 'couponWallReceiveCoupons' },
      });
      // 2. 请求接口
      // 构建 headers：只有当 token 存在且非空时才添加 token header
      const headers: Record<string, string> = {};
      if (userInfo?.token && userInfo.token.trim() !== '') {
        headers.token = userInfo.token;
      }
      
      // 根据场景选择不同的领券接口
      const receiveCouponApi = RECEIVE_COUPON_API_MAP[scene];
      const response = await receiveCouponApi(params, {
        ...(Object.keys(headers).length > 0 ? { headers } : {}),
      });

      
      // 3. 处理接收到的数据
      // 领券成功数量
      const receiveCouponList: Array<CouponData> = [];
      if (response && response.data) {
        const receiveData = response.data;

        // 构建返回数据中领取成功的券的Map，用于快速查找
        // key: `${planCode}_${rightCode}`, value: coupon
        const receivedCouponsMap = new Map<string, CouponData>();
        
        receiveData.tabs.forEach((tab: any) => {
          tab.couponList.forEach((coupon: any) => {
            // 返回的都是已领取的券（有couponViewId）
            if (coupon.couponViewId) {
              const key = `${coupon.planCode}_${coupon.rightCode}`;
              receivedCouponsMap.set(key, coupon);
              receiveCouponList.push(coupon);
            }
          });
        });

        // 合并原有coupons和返回的券
        // 1. 保留原有的已领取券（有couponViewId的）
        // 2. 更新本次领取成功的券（从返回数据中获取）
        // 3. 删除未领取且本次未领取成功的券（原来没有couponViewId，返回数据中也没有的）
        const mergedCoupons: CouponWallItem[] = [];
        data.coupons.forEach((coupon) => {
          if (!coupon.data) return;

          const key = `${coupon.data.planCode}_${coupon.data.rightCode}`;
          const receivedCoupon = receivedCouponsMap.get(key);

          if (coupon.data.couponViewId) {
            // 原有已领取的券，保留
            mergedCoupons.push(coupon);
          } else if (receivedCoupon) {
            // 本次领取成功的券，使用服务端最新数据，但保留本地计算的 isFestivalCoupon 等扩展字段
            mergedCoupons.push({
              type: coupon.type,
              data: {
                ...receivedCoupon,
                isFestivalCoupon: coupon.data.isFestivalCoupon,
              },
            });
          }
          // 如果原来未领取且本次也未领取成功，则不添加到 mergedCoupons 中（被筛掉）
        });

        // 更新Tab列表：以 data.tabs 为准，只保留在 mergedCoupons 中有对应券的 tab
        const newTabs: CouponWallTab[] = data.tabs.filter((tab) => {
          // 检查 mergedCoupons 中是否有对应 planCode 的券（兼容 planCodeList）
          const planCodes = getTabPlanCodes(tab);
          return mergedCoupons.some((coupon) => planCodes.includes(coupon.data?.planCode));
        });

        // 合并quickScreenCouponList：全局的 + 新返回的
        const globalQuickScreenList = quickScreenCouponList || [];
        const newQuickScreenList = receiveData.quickScreenCouponList || [];
        const allQuickScreenList = [...globalQuickScreenList, ...newQuickScreenList];
        
        // 创建Map来存储所有券，key为 `${planCode}_${rightCode}`，后面的券会覆盖前面的
        const quickScreenMap = new Map<string, CouponData>();
        allQuickScreenList.forEach((coupon) => {
          const key = `${coupon.planCode}_${coupon.rightCode}`;
          quickScreenMap.set(key, coupon);
        });
        
        // 按照mergedCoupons的顺序从Map中取出券
        const mergedQuickScreenList: CouponData[] = [];
        mergedCoupons.forEach((coupon) => {
          if (coupon.data) {
            const key = `${coupon.data.planCode}_${coupon.data.rightCode}`;
            const quickScreenCoupon = quickScreenMap.get(key);
            if (quickScreenCoupon) {
              mergedQuickScreenList.push(quickScreenCoupon);
            }
          }
        });

        // 更新state
        setData((prevData) => {
          if (!prevData) return prevData;
          return {
            ...prevData,
            tabs: newTabs,
            coupons: mergedCoupons,
            currentTime: receiveData.currentTime,
            title: receiveData.title || prevData.title,
          };
        });

        // 更新全局 state 中的 quickScreenCouponList
        setQuickScreenCouponList(mergedQuickScreenList);

        // 如果当前选中的tab不存在了，切换到第一个tab
        if (newTabs.length > 0 && !newTabs.find((tab) => tab.planCode === currentTabId)) {
          setCurrentTabId(newTabs[0].planCode);
        }
      }
      setReceiveStatus(ReceiveStatus.SUCCESS);
      // 上报领券后 MC 埋点
      tracker?.moduleClick(COUPON_WALL_BID_MC.AFTER_RECEIVE, {
        button_style: buttonStyle, // 领取方式
        coupon_number: Math.min(4, receiveCouponList.length), // 券数量
        discount_coupon_config_id: receiveCouponList.map(coupon => coupon.couponConfigId).join(','), // 券配置ID
        user_coupon_id: receiveCouponList.map(coupon => coupon.couponViewId).join(','), // 券实例ID
        coupon_channel: lxCouponChannel,
        exposure_state: isHitCouponOptimizeExp ? ExposureStateEnum.PREVIEW : ExposureStateEnum.PRE_SEND, // 券墙渲染状态
      });
      reportCouponWallReceiveSuccessRate(true);
    } catch (err: any) {
      console.error('[CouponWall] 领取失败:', err?.message || err?.data?.message || '领取失败，请稍后重试');
      const errorCode = err?.code || err?.data?.code;
      console.error('[CouponWall] err: ', err, JSON.stringify(err, Object.getOwnPropertyNames(err)));

      // 401 未登录，重新登录
      if (errorCode === 401) {
        setReceiveStatus(ReceiveStatus.IDLE);
        reportCouponWallReceiveSuccessRate(false, "unLogin");
        handleLogin();
      } else {
        // 其他错误，显示错误兜底
        setReceiveStatus(ReceiveStatus.FAIL);
        reportCouponWallReceiveSuccessRate(false, "apiFail");
        toast.show("活动太火爆，请稍后重试哦~");
        // 上报领券后 MC 埋点
        tracker?.moduleClick(COUPON_WALL_BID_MC.AFTER_RECEIVE, {
          button_style: buttonStyle, // 领取方式
          coupon_number: null, // 券数量
          discount_coupon_config_id: '', // 券配置ID
          user_coupon_id: '', // 券实例ID
          coupon_channel: lxCouponChannel,
          exposure_state: isHitCouponOptimizeExp ? ExposureStateEnum.PREVIEW : ExposureStateEnum.PRE_SEND, // 券墙渲染状态
        });
      }
    }
  };

  /**
   * 使用韦恩供给判定接口的返回值更新券的 couponSupply 数据
   * @param couponSupply - 新的 couponSupply 数据
   */
  const handleUpdateCouponSupply = (couponSupply: CouponSupply) => {
    if (!data) {
      console.error('[CouponWall] updateCouponSupply: data is undefined');
      return;
    }
    const newCoupons = data.coupons.map((coupon) => {
      // 根据 wayneId 更新券的 couponSupply 数据
      if (coupon.data.couponSupply?.wayneActId === couponSupply.wayneActId) {
        return {
          ...coupon,
          data: {
            ...coupon.data,
            couponSupply
          }
        };
      }
      return coupon;
    });

    setData((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        coupons: newCoupons,
      };
    });
  }

  // 【新版】处理领券（不需要预发券的结果）
  // 命中实验时使用
  const handleDirectReceiveCoupons = async (buttonStyle: ReceiveCouponButtonStyle) => {
    // 如果正在领取中，防止重复点击
    if (receiveStatus === ReceiveStatus.RECEIVING) {
      return;
    }

    try {
      // 标记状态，如果用户领券时预发券结果还未返回，则不使用预发券的结果
      if (preSendStatus !== PreSendStatus.SUCCESS) {
        isReceivedBeforePreSendRef.current = true;
      }
      setReceiveStatus(ReceiveStatus.RECEIVING);
      // 1. 拼接接口入参
      const clientInfo = await getClientInfo(scene, true);
      const sessionId = await tracker?.getSessionId().catch(()=> '') || ''

      const params: ReceiveCouponV2RequestParams = {
        ...clientInfo,
        sessionId,
        unpl: getUnpl() || '',
        identityScene: {
          sendCouponIdentity: SendCouponIdentityScene.DISTRIBUTION_VENUE,
          sendCouponScene: null,
        }
      };
      reportCustomMetric({
        key: 'receiveCouponUnplStatus',
        value: params.unpl ? 1 : 0,
        tags: { scene: 'couponWallDirectReceiveCoupons' },
      });

      // 2. 请求接口
      // 构建 headers：只有当 token 存在且非空时才添加 token header
      const headers: Record<string, string> = {};
      if (userInfo?.token && userInfo.token.trim() !== '') {
        headers.token = userInfo.token;
      }
      
      const response = await receiveCouponV2(params, {
        ...(Object.keys(headers).length > 0 ? { headers } : {}),
      });

      // 3. 处理接收到的数据
      if (response && response.data) {
        const transformed = transformDataV2(response.data);
        // 没有领取成功的券，Toast 提示异常
        if (!transformed.coupons || !transformed.coupons.length) {
          throw new Error("[couponWall] transformed.coupons.length is 0")
        }

        setData(transformed);
        setQuickScreenCouponList(transformed.quickScreenCouponList);
        
        if (transformed.tabs.length > 0 && !currentTabId) {
          setCurrentTabId(transformed.tabs[0].planCode);
        }

        setReceiveStatus(ReceiveStatus.SUCCESS);
        reportCouponWallReceiveSuccessRate(true);

        // 上报领券后 MC 埋点
        // 本次领取的券
        const newReceivedCoupon = transformed.coupons.filter(coupon => coupon.data.grantSource === CouponGrantSource.NEW);
        tracker?.moduleClick(COUPON_WALL_BID_MC.AFTER_RECEIVE, {
          button_style: buttonStyle, // 领取方式
          coupon_number: Math.min(4, response.data.currentGrantedCount), // 券数量
          discount_coupon_config_id: newReceivedCoupon.map(coupon => coupon.data.couponConfigId).join(','), // 券配置ID
          user_coupon_id: newReceivedCoupon.map(coupon => coupon.data.couponViewId).join(','), // 券实例ID
          coupon_channel: lxCouponChannel,
          exposure_state: isHitCouponOptimizeExp ? ExposureStateEnum.PREVIEW : ExposureStateEnum.PRE_SEND, // 券墙渲染状态
        });
      } else {
        throw new Error('[couponWall] response.data is null');
      }
    } catch (err: any) {
      const errorCode = err?.code || err?.data?.code;
      // 401 未登录，重新登录
      if (errorCode === 401) {
        setReceiveStatus(ReceiveStatus.IDLE);
        reportCouponWallReceiveSuccessRate(false, "unLogin");
        handleLogin();
      } else {
        // 其他错误，显示抄底
        setReceiveStatus(ReceiveStatus.FAIL);
        reportCouponWallReceiveSuccessRate(false, "apiFail");
        toast.show("活动太火爆，请稍后重试哦~");
        // 上报领券后 MC 埋点
        tracker?.moduleClick(COUPON_WALL_BID_MC.AFTER_RECEIVE, {
          button_style: buttonStyle, // 领取方式
          coupon_number: null, // 券数量
          discount_coupon_config_id: '', // 券配置ID
          user_coupon_id: '', // 券实例ID
          coupon_channel: lxCouponChannel,
          exposure_state: isHitCouponOptimizeExp ? ExposureStateEnum.PREVIEW : ExposureStateEnum.PRE_SEND, // 券墙渲染状态
        });
      }
    }
  }

  // 倒计时逻辑（仅在 countdownAnimationToggle 为 true 且存在未领取的券时启动）
  useEffect(() => {
    if (!data?.countdownAnimationToggle || couponWallRenderStatus !== CouponWallRenderStatus.HAS_UNRECEIVED_COUPON) {
      return;
    }

    let timer: NodeJS.Timeout;

    const startCountdown = (count: number) => {
      if (count <= 0) {
        // 倒计时结束，清空倒计时并自动领取
        setCountdown(0);
        handleReceiveCoupons(ReceiveCouponButtonStyle.AUTO);
        return;
      }

      timer = setTimeout(() => {
        const nextCount = count - 1;
        setCountdown(nextCount);
        startCountdown(nextCount);
      }, 1000);
    };

    startCountdown(3); // 3秒倒计时

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponWallRenderStatus, data?.countdownAnimationToggle]);

  // 卡片是否具有底部padding
  const cardHasPaddingBottom = useMemo(() => {
    if (!hasUnreceivedCoupons) return true;
    if (!!data?.countdownAnimationToggle) return true;
    return false;
  }, [hasUnreceivedCoupons, data?.countdownAnimationToggle]);

  // 整个券墙点击埋点
  const handleCouponWallClick = useCallback(() => {
    tracker?.moduleClick(COUPON_WALL_BID_MC.CARD, {
      exposure_state: isHitCouponOptimizeExp ? ExposureStateEnum.PREVIEW : ExposureStateEnum.PRE_SEND,
    });
  }, [tracker, isHitCouponOptimizeExp]);

  // 券墙曝光埋点，仅预发券结果上报，如果是静态图则上报新的埋点
  const handleCouponWallExpose = useCallback(() => {
    reportCustomMetric({
      key: "CouponWallExposeCount",
      value: 1,
      tags: {
        isSupportAPI: typeof window !== "undefined" && "IntersectionObserver" in window,
      }
    })
    tracker?.moduleView(COUPON_WALL_BID_MV.CARD, {
      red_status: redStatus,
      coupon_channel: lxCouponChannel, // 发券渠道
      coupon_number: Math.min(4, data?.coupons?.length || 0), // 展示券数量 1=单张券，2=两张券，3=三张券，4=大于等于4张券
      exposure_state: isHitCouponOptimizeExp ? ExposureStateEnum.PREVIEW : ExposureStateEnum.PRE_SEND,
    });
    recordStep({ stepName: 'web_action_mv_couponwall' });
  }, [tracker, redStatus, lxCouponChannel, data?.coupons?.length, isHitCouponOptimizeExp]);
  
  // 预发券加载中，展示骨架屏
  if (couponWallRenderStatus === CouponWallRenderStatus.LOADING) {
    return (
      <div className="coupon-wall">
        <Skeleton />
      </div>
    )
  }

  // 未登录；命中实验、预发券加载中；预发券接口返回前触发领取中，展示静态图
  if ([CouponWallRenderStatus.NOT_LOGIN, CouponWallRenderStatus.LOADING_PREVIEW, CouponWallRenderStatus.RECEIVING_PREVIEW].includes(couponWallRenderStatus)) {
    return (
      <Exposure onExpose={handleCouponWallExpose}>
        <div className="coupon-wall" onClick={handleCouponWallClick}>
          <PreviewState
            previewImageConfig={couponWallStyle.couponWallPreviewImageConfig}
            previewStatus={PreviewStatusMap[couponWallRenderStatus as keyof typeof PreviewStatusMap]}
            onReceiveCoupon={() => handleDirectReceiveCoupons(ReceiveCouponButtonStyle.MANUAL)}
            onLogin={handleLogin}
          />
        </div>
      </Exposure>
    )
  }

  // 错误兜底态
  if (couponWallRenderStatus === CouponWallRenderStatus.ERROR || !data || !data.coupons.length) {
    return (
      <Exposure onExpose={handleCouponWallExpose}>
        <div className="coupon-wall" onClick={handleCouponWallClick}>
          <EmptyState handleClick={() => fetchCouponWall(true)} />
        </div>
      </Exposure>
    );
  }

  // 正常展示数据
  return (
    <Exposure onExpose={handleCouponWallExpose}>
      <div className="coupon-wall" onClick={handleCouponWallClick}>
        <div className={`coupon-wall-content ${cardHasPaddingBottom ? 'coupon-wall-content-padding-bottom' : ''} ${useSingleCouponStyle ? 'coupon-wall-content-single' : ''}`}>
          {/* 标题区域：单券不展示 */}
          {!useSingleCouponStyle && data.title && data.title.length > 0 && (
            <div className="coupon-wall-title">
              {
                data.title.map((item) => (
                  <span key={item.text} style={{ color: item.color }}>{item.text}</span>
                ))
              }
            </div>
          )}
          
          {/* Tab列表 */}
          {(data.tabs && data.tabs.length > 1) && (
            <div className="coupon-wall-tabs">
              <ScrollView
                className="coupon-wall-tabs-scroll"
                onScrollEnd={handleTabsScrollEnd}
              >
                {data.tabs.map((tab, index) => (
                  <Tab
                    key={tab.planCode}
                    tab={tab}
                    tabIndex={index}
                    isActive={currentTabId === tab.planCode}
                    onClick={handleTabClick}
                    distributorChannel={lxCouponChannel}
                    useTwoRowCouponStyle={useTwoRowCouponStyle}
                  />
                ))}
              </ScrollView>
            </div>
          )}
          
          {/* 券列表区域 */}
          {
            useSingleCouponStyle
            ? ( // 单券
              <div className="coupon-wall-coupon-list-single">
                <CouponCard
                  key={`coupon_${data.coupons[0].data?.rightCode || 0}_${data.coupons[0].data?.planCode || ''}_${data.coupons[0].data?.couponViewId}`}
                  scene={scene}
                  style={CouponCardStyle.SINGLE}
                  coupon={data.coupons[0]}
                  couponIndex={0}
                  tab={data.tabs[0]}
                  redStatus={redStatus}
                  currentTime={data.currentTime}
                  showCouponValue={data.showCouponValue}
                  hasUnreceivedCoupons={hasUnreceivedCoupons}
                  distributorChannel={lxCouponChannel}
                  autoReceiveCountdown={hasUnreceivedCoupons && data.countdownAnimationToggle ? countdown : 0}
                  onReceiveCoupon={() => handleReceiveCoupons(ReceiveCouponButtonStyle.MANUAL)}
                  onUpdateCouponSupply={handleUpdateCouponSupply}
                />
              </div>
            )
            : ( // 多券
              <div className="coupon-wall-coupon-list-multi">
                <div className={`coupon-wall-coupons-scroll ${enableFewCouponStyleFit ? 'coupon-wall-coupons-scroll-fit-few-coupon' : ''} ${useTwoRowCouponStyle ? 'coupon-wall-coupons-scroll-two-row' : ''}`} ref={couponsScrollRef}>
                  {data.coupons.map((coupon, index) => {
                    // 根据 planCode 找到对应的 tab（兼容 planCodeList）
                    const tab = data.tabs.find(t => getTabPlanCodes(t).includes(coupon.data?.planCode));
                    return (
                      <CouponCard
                        key={`coupon_${coupon.data?.rightCode || index}_${coupon.data?.planCode || ''}_${coupon.data?.couponViewId}_${index}`}
                        scene={scene}
                        // 优化实验下，展示两行券，券卡片采用紧凑的样式
                        style={useTwoRowCouponStyle ? CouponCardStyle.COMPACT : CouponCardStyle.DEFAULT}
                        coupon={coupon}
                        couponIndex={index}
                        tab={tab}
                        redStatus={redStatus}
                        currentTime={data.currentTime}
                        showCouponValue={data.showCouponValue}
                        hasUnreceivedCoupons={hasUnreceivedCoupons}
                        distributorChannel={lxCouponChannel}
                        enableFitFewCoupon={enableFewCouponStyleFit}
                        onUpdateCouponSupply={handleUpdateCouponSupply}
                      />
                    );
                  })}
                </div>
              </div>
            )
          }
          {/* 底部按钮区域（仅当存在未领取的券，并且非单券样式时显示） */}
          {hasUnreceivedCoupons && !useSingleCouponStyle && (
            <div className={`coupon-wall-bottom ${!cardHasPaddingBottom ? 'coupon-wall-bottom-no-padding' : ''}`}>
              <img className='coupon-wall-bottom-bg' src={couponWallStyle.couponWallBottomBgUrl} />
              <div 
                className={`coupon-wall-bottom-button ${couponWallRenderStatus === CouponWallRenderStatus.RECEIVING ? 'coupon-wall-bottom-button-disabled' : ''}`}
                onClick={() => handleReceiveCoupons(ReceiveCouponButtonStyle.MANUAL)}
              >
                {couponWallRenderStatus === CouponWallRenderStatus.RECEIVING ? '领取中...' : '立即领取'}
              </div>
              {(data.countdownAnimationToggle && countdown > 0) && (
                <div className='coupon-wall-bottom-countdown'>
                  倒计时<span className='coupon-wall-bottom-countdown-time'>{countdown}</span>秒后自动领取
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Exposure>
  );
};

export default CouponWall;